import React from 'react'
import {InView, useInView} from 'react-intersection-observer'
import styled from 'styled-components'

import {homepageStickyNavActioned} from '@/analytics/segment'
import Button from '@/components/button'
import Image from '@/components/image'
import RichText from '@/components/rich-text'
import SmoothScrollLink from '@/components/smooth-scroll-link'
import {Step, StyledBody} from '@/components/step'
import {Text, text} from '@/components/text'
import type {StickyNavSectionsFragment, StickyNavSectionsSecondaryFragment} from '@/graphql-types'
import arrowDownIcon from '@/images/icons/navigate-down.svg'
import {useSignupModal} from '@/providers/signup-modal'
import {StepsWrapperStyles} from '@/sections/steps'
import {Container, Section} from '@/styles/grid'
import {focusStyles} from '@/styles/utils'
import {colors, headerSizes, radii, tokens, zIndex} from '@/theme'
import mq from '@/utils/media-query'
import {stringToSlug} from '@/utils/string-to-slug'

interface Props {
    slice: StickyNavSectionsFragment | StickyNavSectionsSecondaryFragment
}

type ActiveSectionProps = {
    index: number
    title: StickyNavSectionsFragment['items'][0]['title']
}

const MOBILE_BP = 'sm'
const TABLET_BP = 'md'

export const StickyNavSections = React.forwardRef<HTMLDivElement, Props>(
    ({slice: {primary, items}}: Props, ref) => {
        const data = items.map((item, index) => {
            const cards = [
                ...(item.card_1_title
                    ? [
                          {
                              image: {
                                  ...item.card_1_image,
                              },
                              heading: item.card_1_title,
                              content: item.card_1_body,
                              videoWebM: item.card_1_video_webm,
                              videoMp4: item.card_1_video_mp4,
                              videoPoster: item.card_1_video_poster,
                          },
                      ]
                    : []),
                ...(item.card_2_title
                    ? [
                          {
                              image: {
                                  ...item.card_2_image,
                              },
                              heading: item.card_2_title,
                              content: item.card_2_body,
                              videoWebM: item.card_2_video_webm,
                              videoMp4: item.card_2_video_mp4,
                              videoPoster: item.card_2_video_poster,
                          },
                      ]
                    : []),
                ...(item.card_3_title
                    ? [
                          {
                              image: {
                                  ...item.card_3_image,
                              },
                              heading: item.card_3_title,
                              content: item.card_3_body,
                              videoWebM: item.card_3_video_webm,
                              videoMp4: item.card_3_video_mp4,
                              videoPoster: item.card_3_video_poster,
                          },
                      ]
                    : []),
            ]

            return {
                section: {
                    index,
                    id: stringToSlug(item.title + index),
                    title: item.title,
                    subtitle: item.subtitle.richText,
                    image: item.image,
                },
                cards,
            }
        })

        const {renderSignupModal} = useSignupModal()
        const [navRef, navInView] = useInView()
        const [animationRef, animateWidth] = useInView()
        const [isOpen, setIsOpen] = React.useState(false)
        const [overrideInView, setOverrideInView] = React.useState(false)
        const [activeSection, setActiveSection] = React.useState<ActiveSectionProps>({
            index: 0,
            title: data[0].section.title,
        })

        const renderNavMobileButton = (
            <NavMobileButton onClick={() => setIsOpen(!isOpen)}>
                <Inline>
                    {activeSection.title}
                    <img
                        src={arrowDownIcon}
                        alt=""
                        width={20}
                        style={{transform: isOpen ? 'rotate(180deg)' : 'initial'}}
                    />
                </Inline>
            </NavMobileButton>
        )

        return (
            <div ref={ref}>
                <HiddenDesktop>
                    <Text $variant="heading-large">{primary.navigation_text}</Text>
                </HiddenDesktop>

                <NavStickyWrapper $isOpen={navInView || isOpen}>
                    <NavScrollTrigger ref={navRef} />
                    <AnimationScrollTrigger ref={animationRef} />

                    {renderNavMobileButton}

                    <NavContainer $isOpen={isOpen}>
                        {isOpen && renderNavMobileButton}

                        <NavContent style={{width: animateWidth ? '100%' : '95%'}}>
                            <NavTitle $variant="paragraph-large" $center $isVisible={isOpen}>
                                {primary.navigation_text}
                            </NavTitle>

                            <Nav>
                                {data.map(({section}, index) => (
                                    <NavItem
                                        key={`${section.title}-${index}`}
                                        forwardedAs={SmoothScrollLink}
                                        selector={`#${section.id}`}
                                        verticalAlignment="start"
                                        onClick={() => {
                                            setOverrideInView(true)
                                            setActiveSection({index, title: section.title})
                                            setIsOpen(false)
                                            homepageStickyNavActioned({
                                                action: index + 1,
                                            })
                                        }}
                                        $variant={
                                            activeSection.index === index ? 'default' : 'outline'
                                        }
                                    >
                                        {section.title}
                                    </NavItem>
                                ))}

                                <NavActiveIndicator
                                    $width={`${100 / data.length}%`}
                                    style={{
                                        transform: `translateX(${activeSection.index * 100}%)`,
                                    }}
                                />
                            </Nav>

                            {isOpen && <NavBackdrop onTouchStart={() => setIsOpen(false)} />}

                            {(animateWidth || isOpen) && (
                                <NavCTA
                                    onClick={() => {
                                        renderSignupModal()
                                        setIsOpen(false)
                                    }}
                                >
                                    {primary.cta_text}
                                </NavCTA>
                            )}
                        </NavContent>
                    </NavContainer>
                </NavStickyWrapper>

                {data.map(({section, cards}, index) => (
                    <StyledSection key={`${section.title}-${index}`} id={section.id}>
                        <SectionIntersectionTrigger>
                            <InView
                                style={{height: '100%'}}
                                initialInView={index === 0}
                                onChange={(inView) => {
                                    if (inView) {
                                        if (!overrideInView) {
                                            setActiveSection({index, title: section.title})
                                        }

                                        if (activeSection.index === index) {
                                            setOverrideInView(false)
                                        }
                                    }
                                }}
                            />
                        </SectionIntersectionTrigger>

                        <HeadingArea>
                            {section.title && (
                                <Text as="h2" $variant="heading-large">
                                    {section.title}
                                </Text>
                            )}
                            {section.subtitle && <StyledRichText content={section.subtitle} />}
                            {section.image && (
                                <StyledImage objectFit="cover" image={section.image} />
                            )}
                        </HeadingArea>

                        <StyledStepsWrapper>
                            {cards.map((card, index) => (
                                <StyledStep
                                    key={`${card.heading}-${index}`}
                                    backgroundColor="muted"
                                    {...card}
                                />
                            ))}
                        </StyledStepsWrapper>
                    </StyledSection>
                ))}
            </div>
        )
    },
)

const HiddenDesktop = styled(Container)`
    margin-top: ${tokens.spacing40};

    ${mq.min(TABLET_BP)} {
        display: none;
    }
`

const NavStickyWrapper = styled.div<{$isOpen: boolean}>`
    position: sticky;
    top: 0;
    left: 0;
    z-index: ${(props) => (props.$isOpen ? zIndex.header : zIndex.header + 1)};
    background-color: ${colors.background.default};

    ${mq.min(MOBILE_BP)} {
        z-index: ${(props) => (props.$isOpen ? zIndex.header : 1)};
        padding: ${tokens.spacing16} ${tokens.spacing24};
        margin-top: ${tokens.spacing40};
    }

    ${mq.min(TABLET_BP)} {
        padding: ${tokens.spacing16} ${tokens.spacing40};
        margin-top: 80px;
        margin-bottom: 80px;
    }
`

const NavContainer = styled(Container)<{$isOpen: boolean}>`
    padding-left: 0;
    padding-right: 0;

    ${mq.max(MOBILE_BP)} {
        display: ${(props) => (props.$isOpen ? 'grid' : 'none')};
        grid-template-rows: max-content auto;
        position: fixed;
        inset: 0;
        overflow: auto;
        overscroll-behavior: contain;
        z-index: 1;
        background-color: ${colors.background.default};
    }
`

const NavBackdrop = styled.div`
    flex-grow: 1;

    ${mq.min(MOBILE_BP)} {
        display: none;
    }
`

const NavContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${tokens.spacing20};

    ${mq.max(MOBILE_BP)} {
        height: 100%;
        padding: ${tokens.spacing2} ${tokens.spacing24} ${tokens.spacing32};
    }

    ${mq.min(MOBILE_BP)} {
        margin-right: auto;
        margin-left: auto;
        transition: width ${tokens.fastIn};
        flex-direction: row;
        align-items: center;
    }
`

const NavTitle = styled(Text)<{$isVisible: boolean}>`
    ${mq.max(MOBILE_BP)} {
        display: ${(props) => (props.$isVisible ? 'block' : 'none')};
    }

    ${mq.between(MOBILE_BP, TABLET_BP)} {
        display: none;
    }

    ${mq.min(TABLET_BP)} {
        &::after {
            content: ' →';
        }
    }
`

const Nav = styled.nav`
    display: grid;
    grid-template-columns: 1fr;
    gap: ${tokens.spacing20};
    position: relative;

    ${mq.min(MOBILE_BP)} {
        display: inline-grid;
        grid-auto-columns: minmax(0, 1fr);
        grid-auto-flow: column;
        gap: 0;
        border: ${({theme}) => theme.borders.secondary};
        border-radius: ${radii.circle};
    }
`

const NavItem = styled(Button)`
    position: relative;

    ${mq.min(MOBILE_BP)} {
        background-color: ${colors.background.default};
        color: ${colors.text.default};
        border: none;
        border-radius: 0;

        &:first-of-type {
            border-top-left-radius: ${radii.circle};
            border-bottom-left-radius: ${radii.circle};
        }

        &:last-of-type {
            border-top-right-radius: ${radii.circle};
            border-bottom-right-radius: ${radii.circle};
        }

        &::before {
            content: '';
            position: absolute;
            inset: 2px;
            border-radius: ${radii.circle};
        }

        &:focus-visible {
            box-shadow: none;

            &::before {
                ${focusStyles()};
            }
        }

        &:hover,
        &:active {
            color: currentcolor;
            background: ${colors.background.default};
        }
    }
`

const NavActiveIndicator = styled.span<{$width: string}>`
    ${mq.max(MOBILE_BP)} {
        display: none;
    }

    position: absolute;
    top: 0;
    left: 0;
    width: ${(props) => props.$width};
    height: 100%;
    border-radius: inherit;
    background-color: ${colors.background.default};
    mix-blend-mode: difference;
    transition: transform ${tokens.fastIn};
`

const NavCTA = styled(Button)`
    ${mq.min(MOBILE_BP)} {
        margin-left: auto;
    }
`

const NavScrollTrigger = styled.span`
    position: absolute;
    z-index: ${zIndex.observerElement};
    top: calc(100dvh - ${headerSizes.heightSmall}px);
    height: ${headerSizes.heightSmall}px;

    ${mq.min(headerSizes.breakpoint)} {
        top: calc(100vh - ${headerSizes.heightLarge}px);
        height: ${headerSizes.heightLarge}px;
    }
`

const AnimationScrollTrigger = styled.span`
    position: absolute;
    z-index: ${zIndex.observerElement};
    top: 100dvh;
`

const NavMobileButton = styled.button`
    ${text({$variant: 'title-base'})}
    white-space: nowrap;
    width: 100%;
    height: ${headerSizes.heightSmall}px;
    padding: ${tokens.spacing12} ${tokens.spacing24};
    background-color: ${colors.background.default};
    border: none;

    ${mq.min(MOBILE_BP)} {
        display: none;
    }
`

const Inline = styled.span`
    display: flex;
    gap: ${tokens.spacing8};
    justify-content: space-between;
    width: 100%;
`

const SectionIntersectionTrigger = styled.span`
    position: absolute;
    height: calc(100% - 100dvh - ${headerSizes.heightSmall}px);
    top: calc(100dvh - ${headerSizes.heightSmall}px);
    z-index: ${zIndex.observerElement};
`

const StyledStep = styled(Step)`
    font-size: ${tokens.fontXLarge};

    ${mq.min(MOBILE_BP)} {
        font-size: ${tokens.fontLarge};
    }

    & ${StyledBody} {
        font-size: ${tokens.fontXLarge};

        ${mq.min(MOBILE_BP)} {
            font-size: ${tokens.fontLarge};
        }
    }
`

const StyledSection = styled(Section)`
    display: grid;
    gap: ${tokens.spacing20};
    padding-top: 80px;
    padding-bottom: 80px;
    position: relative;

    ${mq.min(MOBILE_BP)} {
        gap: ${tokens.spacing60};
    }
`

const StyledStepsWrapper = styled.div`
    ${StepsWrapperStyles}
    gap: ${tokens.spacing20};
    grid-auto-rows: unset;

    ${mq.min(MOBILE_BP)} {
        gap: ${tokens.spacing60};
    }

    ${mq.min('lg')} {
        grid-auto-columns: minmax(0, 1fr);
        grid-auto-flow: column;
    }
`

const HeadingArea = styled.div`
    display: grid;
    gap: ${tokens.spacing24};

    ${mq.min(MOBILE_BP)} {
        text-align: center;
        gap: ${tokens.spacing40};
    }
`

const StyledRichText = styled(RichText)`
    ${mq.min(MOBILE_BP)} {
        max-width: 840px;
        text-align: center;
        display: grid;
        justify-self: center;
    }
`

const StyledImage = styled(Image)`
    ${mq.max(MOBILE_BP)} {
        aspect-ratio: 1/1;
    }
`
